import { getWindArrow } from 'features/Weather/utils/windArrow.utils';
import { DateToString } from 'utils/strings/string.utils';

export const formatForecastChartLabel = (forecast) => {
  const date = DateToString(new Date(forecast.iso8601));
  return `${date.weekday_short}, ${date.day_short} ${date.month_short}`;
};

export const buildMetricChartOptions = (title) => ({
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  title: { display: true, text: title },
});

export const buildWindSpeedChartOptions = (chartOptions, windDirectionDatasetIndex) => ({
  ...chartOptions,
  plugins: {
    windDirectionIcon: {
      enabled: true,
      datasets: [windDirectionDatasetIndex],
      iconStyle: { color: 'black' },
    },
    tooltip: { callbacks: windTooltipCallbacks },
  },
});

// Reuse one callback object across generated charts to keep tooltip updates stable.
const windTooltipCallbacks = {
  label: (context) => {
    if (context.dataset.label?.includes('Wind Direction')) {
      return `${context.dataset.label}: ${context.raw?.displayValue || 'N/A'}`;
    }

    const value = context.parsed?.y;
    return `${context.dataset.label}: ${typeof value === 'number' ? value.toFixed(2) : value || 'N/A'}`;
  },
};

/** Build the shared display options for detailed, hourly, and daily forecast charts. */
export const buildForecastChartOptions = (
  forecast,
  {
    period,
    datasets,
    pluginOptions,
    weatherIconStyle,
    windIconStyle,
    textFillStyle,
    weatherIconSkip,
    windDirectionIconSkip,
  },
) => ({
  responsive: true,
  maintainAspectRatio: false,
  interaction: { mode: 'index', intersect: false },
  parsing: { xAxisKey: 'key', yAxisKey: 'value' },
  plugins: {
    ...pluginOptions,
    tooltip: {
      callbacks: {
        title: (contexts) => formatForecastDateTime(forecast[contexts[0].dataIndex]),
        label: formatForecastTooltipValue,
      },
    },
    // Resolve plugin targets from the datasets because daily charts add high/low lines.
    textAtPoint: {
      enabled: true,
      datasets: [datasets.findIndex(({ label }) => label === 'Precipitation')],
      fillStyle: textFillStyle,
    },
    weatherIconAtPoint: {
      enabled: true,
      datasets: [datasets.findIndex(({ label }) => label === 'Temperature')],
      iconIds: forecast.map(({ icon }) => icon),
      iconStyle: weatherIconStyle,
      ...(period === 'hourly' ? { step: weatherIconSkip } : {}),
    },
    windDirectionIcon: {
      enabled: true,
      datasets: [datasets.findIndex(({ label }) => label === 'Wind Direction')],
      iconIds: forecast.map(({ icon }) => icon),
      iconStyle: windIconStyle,
      ...(period === 'hourly' ? { step: windDirectionIconSkip } : {}),
    },
    customTooltip: { enabled: true },
  },
  scales: {
    ...buildForecastTimeScales(forecast, period),
    yWindDir: {
      display: false,
      min: 0,
      max: 2,
      stack: 'wind',
      stackWeight: 1,
      offset: true,
    },
    y: {
      beginAtZero: true,
      suggestedMin: 0,
      suggestedMax: 100,
      ticks: { callback: (value) => `${value}°` },
      stack: 'wind',
      stackWeight: 9,
    },
    yIgnore: { display: false, stack: 'precip', stackWeight: 1 },
    yPrecip: {
      display: false,
      beginAtZero: true,
      stack: 'precip',
      stackWeight: 9,
      ...(period === 'hourly' ? { suggestedMax: 0.05 } : {}),
    },
  },
});

const formatForecastDateTime = (forecast) => {
  const date = new Date(forecast.iso8601);
  const { month_short, day_short } = DateToString(date);
  const time = date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  return `${month_short} ${day_short} ${time}`;
};

const formatForecastTooltipValue = (context) => {
  if (context.dataset.label === 'Wind Direction') {
    const windDirection = context.raw?.windDirection;
    if (windDirection || windDirection === 0) {
      return `Wind Direction: ${getWindArrow(windDirection)} ${windDirection}°`;
    }
    return 'Wind Direction: N/A';
  }

  const label = context.dataset.label || '';
  const value = context.parsed?.y;
  return `${label}: ${typeof value === 'number' ? value.toFixed(2) : value || 'N/A'}`;
};

const buildForecastTimeScales = (forecast, period) => {
  if (period === 'daily') {
    return { x: { labels: forecast.map(formatForecastDateTime) } };
  }

  const dayScales = buildForecastDayScales(forecast, period);
  if (period === 'detailed') {
    return { x: { labels: forecast.map(formatForecastDateTime) }, ...dayScales };
  }

  return {
    // Hourly charts label temperatures every 3 hours and wind directions every 6 hours.
    x: { labels: getHourlyLabels(forecast, 3), grid: { offset: true } },
    xBar: {
      display: false,
      labels: forecast.map((_, index) => index),
      grid: { offset: true },
    },
    xSkip: {
      display: false,
      labels: getHourlyLabels(forecast, 6),
      grid: { offset: true },
    },
    ...dayScales,
  };
};

const getHourlyLabels = (forecast, intervalHours) =>
  forecast.map((item) =>
    new Date(item.iso8601).getHours() % intervalHours === 0 ? formatForecastDateTime(item) : '',
  );

const buildForecastDayScales = (forecast, period) => {
  const dayScales = {};
  const scalesByDay = new Map();
  const isHourly = period === 'hourly';

  // Count each day's points in one pass, retaining the API's original day order.
  forecast.forEach((item) => {
    const date = DateToString(new Date(item.iso8601));
    const dayKey = isHourly ? date.day : item.weekday;
    const existingScale = scalesByDay.get(dayKey);
    if (existingScale) {
      existingScale.stackWeight += 1;
      return;
    }

    const scale = {
      ...(isHourly ? { day: date.day } : { weekday: item.weekday }),
      type: 'category',
      labels: [
        isHourly
          ? `${date.weekday_short} ${date.month_short} ${date.day}`
          : formatForecastDateTime(item),
      ],
      stack: 'days',
      stackWeight: 1,
      grid: { lineWidth: 2, color: 'black', ...(isHourly ? { offset: true } : {}) },
    };
    dayScales[`x${scalesByDay.size}`] = scale;
    scalesByDay.set(dayKey, scale);
  });

  return dayScales;
};
